import { useState, useEffect } from 'react';
import { supabase } from './supabase';

const T = {
  bg:'#06060d',s1:'#0d0c18',s2:'#12111e',s3:'#181628',
  border:'rgba(255,255,255,0.07)',border2:'rgba(255,255,255,0.13)',
  gold:'#d4a853',gold2:'#f0c878',cream:'#f0ead8',
  text2:'#9890b0',text3:'#55506a',teal:'#1a8a76',purple:'#8b5cf6',
};

export default function PressPage({ handle }) {
  const [profile, setProfile] = useState(null);
  const [rateItems, setRateItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Load rate card from localStorage (will move to Supabase)
      const savedRates = localStorage.getItem('rate_card_' + 'public');
      const savedPub = localStorage.getItem('rate_card_public_' + 'public');
      const cleanHandle = ('@' + handle.replace(/^@/, '')).toLowerCase();
      console.log('PressPage: looking for handle:', cleanHandle);
      const { data, error } = await supabase.from('profiles')
        .select('id, handle, display_name, avatar_url, crafts, bio, location, press_bio, press_quote, press_awards, account_type, account_subtype, press_public, featured_work, created_at')
        .ilike('handle', cleanHandle)
        .maybeSingle();
      console.log('PressPage: data:', data, 'error:', error);
      if (error || !data) { setNotFound(true); setLoading(false); return; }
      if (data.press_public === false) { setNotFound(true); setLoading(false); return; }
      setProfile(data);
      // Rate card will come from Supabase in next pass
      // For now show empty — public rate card stored per user not per handle
      setLoading(false);
    };
    load();
  }, [handle]);

  if (loading) return (
    <div style={{minHeight:'100vh',background:T.bg,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'DM Sans','Jost',sans-serif"}}>
      <div style={{color:T.gold,fontSize:24}}>✦</div>
    </div>
  );

  if (notFound) return (
    <div style={{minHeight:'100vh',background:T.bg,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',fontFamily:"'DM Sans','Jost',sans-serif"}}>
      <div style={{fontSize:32,marginBottom:12}}>✦</div>
      <div style={{color:T.cream,fontSize:18,fontWeight:700,marginBottom:8}}>Press page not found</div>
      <div style={{color:T.text2,fontSize:13,marginBottom:24}}>This profile may not exist or their press page is private.</div>
      <a href="https://artizancraft.app" style={{color:T.gold,fontSize:12,textDecoration:'none'}}>← Back to Artizan</a>
    </div>
  );

  const pressBio = profile.press_bio || profile.bio || 'No press bio available.';
  const crafts = (profile.crafts || []).join(' · ');
  const location = profile.location || '';
  const score = profile.verification_score || 0;

  return (
    <div style={{minHeight:'100vh',background:T.bg,fontFamily:"'DM Sans','Jost',sans-serif"}}>
      {/* Header bar */}
      <div style={{background:T.s1,borderBottom:`1px solid ${T.border}`,padding:'12px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{color:T.gold,fontSize:18}}>✦</span>
          <span style={{color:T.cream,fontSize:14,fontWeight:700}}>Artizan</span>
          <span style={{color:T.text3,fontSize:12,marginLeft:4}}>Press Kit</span>
        </div>
        <a href="https://artizancraft.app" style={{color:T.text2,fontSize:11,textDecoration:'none',border:`1px solid ${T.border2}`,borderRadius:6,padding:'4px 10px'}}>Join Artizan →</a>
      </div>

      <div style={{maxWidth:720,margin:'0 auto',padding:'40px 24px'}}>
        {/* Hero */}
        <div style={{display:'flex',gap:24,alignItems:'flex-start',marginBottom:40}}>
          <div style={{width:100,height:100,borderRadius:'50%',background:T.s3,border:`2px solid ${T.gold}`,overflow:'hidden',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:36}}>
            {profile.avatar_url ? <img src={profile.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : '👤'}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:28,fontWeight:700,color:T.cream,marginBottom:4}}>{profile.display_name || profile.handle}</div>
            <div style={{fontSize:14,color:T.gold,marginBottom:6}}>{crafts}</div>
            {location && <div style={{fontSize:12,color:T.text2,marginBottom:8}}>📍 {location}</div>}
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {score >= 75 && <span style={{background:`${T.gold}20`,color:T.gold,border:`1px solid ${T.gold}40`,borderRadius:4,padding:'2px 8px',fontSize:10,fontWeight:700}}>✦ VERIFIED ARTISAN</span>}
              {profile.account_subtype && <span style={{background:`${T.teal}20`,color:T.teal,border:`1px solid ${T.teal}40`,borderRadius:4,padding:'2px 8px',fontSize:10,fontWeight:700}}>{profile.account_subtype.toUpperCase()}</span>}
            </div>
          </div>
        </div>

        {/* Press Quote */}
        {profile.press_quote && (
          <div style={{background:T.s2,border:`1px solid ${T.gold}30`,borderRadius:12,padding:'20px 24px',marginBottom:28,position:'relative'}}>
            <div style={{fontSize:32,color:T.gold,lineHeight:1,marginBottom:8,opacity:0.5}}>"</div>
            <div style={{fontSize:15,color:T.cream,lineHeight:1.8,fontStyle:'italic'}}>{profile.press_quote}</div>
          </div>
        )}

        {/* Bio */}
        <div style={{marginBottom:28}}>
          <div style={{fontSize:11,color:T.gold,fontWeight:700,textTransform:'uppercase',letterSpacing:2,marginBottom:10}}>Biography</div>
          <div style={{fontSize:14,color:T.text2,lineHeight:1.9}}>{pressBio}</div>
        </div>

        {/* Awards / Credits */}
        {profile.press_awards && (
          <div style={{marginBottom:28}}>
            <div style={{fontSize:11,color:T.gold,fontWeight:700,textTransform:'uppercase',letterSpacing:2,marginBottom:10}}>Awards & Credits</div>
            <div style={{fontSize:14,color:T.text2,lineHeight:1.9,whiteSpace:'pre-line'}}>{profile.press_awards}</div>
          </div>
        )}

        {/* Featured Work */}
        {(profile.featured_work || []).length > 0 && (
          <div style={{marginBottom:28}}>
            <div style={{fontSize:11,color:T.gold,fontWeight:700,textTransform:'uppercase',letterSpacing:2,marginBottom:12}}>Featured Work</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
              {profile.featured_work.slice(0,6).map((w,i)=>(
                <div key={i} style={{background:T.s2,border:`1px solid ${T.border}`,borderRadius:10,padding:'12px 14px'}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.cream,marginBottom:2}}>{w.title||'Untitled'}</div>
                  <div style={{fontSize:11,color:T.text3}}>{w.role||''}{w.year?' · '+w.year:''}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact / Links */}
        <div style={{background:T.s2,border:`1px solid ${T.border2}`,borderRadius:12,padding:'16px 20px',marginBottom:28}}>
          <div style={{fontSize:11,color:T.gold,fontWeight:700,textTransform:'uppercase',letterSpacing:2,marginBottom:10}}>Press Contact</div>
          <div style={{fontSize:13,color:T.text2,lineHeight:1.8}}>
            For press inquiries, interview requests, and media assets, contact through Artizan.
          </div>
          <a href={'https://artizancraft.app'} style={{display:'inline-block',marginTop:10,background:`linear-gradient(135deg,${T.gold},${T.gold2})`,color:T.bg,fontWeight:700,fontSize:12,padding:'8px 16px',borderRadius:8,textDecoration:'none'}}>
            View Full Profile on Artizan →
          </a>
        </div>

        {/* Artizan branding */}
        <div style={{textAlign:'center',paddingTop:20,borderTop:`1px solid ${T.border}`}}>
          <div style={{fontSize:11,color:T.text3}}>Press page powered by <span style={{color:T.gold}}>Artizan</span> — The verified creative professional network</div>
          <div style={{fontSize:10,color:T.text3,marginTop:4}}>artizancraft.app</div>
        </div>
      </div>
    </div>
  );
}